import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import Api from '../../../../../api/Api';
import * as _ from 'lodash';
import { dispatchError } from '../../../../../services/mini/dispatchError';

export const useCrawlerTasks = (projectId) => {
    const [tasks, setTasks] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    const getTasks = useCallback(async (skipLoading?: boolean) => {
        if (skipLoading !== true) {
            setIsLoading(true);
        }

        try {
            const result = await Api.getCrawlerTasks({ projectId });

            if (result?.tasks) {
                const sortedTasks = _.sortBy(result.tasks.map(el => ({ ...el, createTs: +el.createTs })), 'createTs');
                setTasks(sortedTasks.reverse());
            }
        } catch (error) {
            dispatchError(error);
        } finally {
            setIsLoading(false);
        }
    }, [projectId]);

    const stopTask = useCallback(async (projectId, uniqueKey) => {
        setIsLoading(true);
        try {
            await Api.stopCrawlerTask({ projectId, uniqueKey });
            getTasks();
        } catch (error) {
            dispatchError(error);
        } finally {
            setIsLoading(false);
        }
    }, []);

    const stopAllTasks = useCallback(async (projectId, uniqueKeys) => {
        setIsLoading(true);
        try {
            for (let i = 0; i < uniqueKeys.length; i++) {
                await Api.stopCrawlerTask({ projectId, uniqueKey: uniqueKeys[i] });
            }
            getTasks();
        } catch (error) {
            dispatchError(error);
        } finally {
            setIsLoading(false);
        }
    }, []);

    const restartAllTasks = useCallback(async (projectId, uniqueKeys) => {
        setIsLoading(true);
        try {
            for (let i = 0; i < uniqueKeys.length; i++) {
                await Api.restartCrawlerTask({ projectId, uniqueKey: uniqueKeys[i] });
            }
            getTasks();
        } catch (error) {
            dispatchError(error);
        } finally {
            setIsLoading(false);
        }
    }, []);

    const deleteAllTasks = useCallback(async (projectId, uniqueKeys) => {
        setIsLoading(true);
        try {
            for (let i = 0; i < uniqueKeys.length; i++) {
                await Api.deleteCrawlerTask({ projectId, uniqueKey: uniqueKeys[i] });
            }
            getTasks();
        } catch (error) {
            dispatchError(error);
        } finally {
            setIsLoading(false);
        }
    }, []);

    const deleteTask = useCallback(async (projectId, uniqueKey) => {
        setIsLoading(true);
        try {
            await Api.deleteCrawlerTask({ projectId, uniqueKey });
            getTasks();
        } catch (error) {
            dispatchError(error);
        } finally {
            setIsLoading(false);
        }
    }, []);

    const restartTask = useCallback(async (projectId, uniqueKey) => {
        setIsLoading(true);
        try {
            await Api.restartCrawlerTask({ projectId, uniqueKey });
            getTasks();
        } catch (error) {
            dispatchError(error);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        getTasks();
    }, [getTasks]);

    useEffect(() => {
        const interval = setInterval(async () => {
            await getTasks(true);
        }, 3000);

        return () => clearInterval(interval);
    }, [getTasks]);

    return {
        tasks,
        getTasks,
        stopTask,
        deleteTask,
        restartTask,
        stopAllTasks,
        restartAllTasks,
        deleteAllTasks,
        isLoading
    }
}